import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, PieChart, Zap, Wallet, Bitcoin, Landmark, Percent, Crosshair,
  Receipt, Layers, ArrowLeftRight, BookOpen, ShieldCheck, Sliders, TrendingUp, FlaskConical,
  Link as LinkIcon, Settings, LogOut, OctagonX, Target, Menu, X, Newspaper, Building2, ListChecks, Swords, Lock,
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CelebrationProvider } from "@/context/CelebrationContext";
import { InstallAppButton } from "@/components/InstallAppButton";
import { applyUpdate } from "@/lib/pwaInstall";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { t } from "@/i18n";
import { toast } from "@/components/ui/sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const NAV = [
  { to: "/command-center", label: "Command Center", labelKey: "nav.commandCenter", icon: Building2 },
  { to: "/", label: "Overview", labelKey: "nav.overview", icon: LayoutDashboard, end: true },
  { to: "/portfolio", label: "Portfolio", labelKey: "nav.portfolio", icon: PieChart },
  { to: "/deploy", label: "Deploy Capital", labelKey: "nav.deploy", icon: Zap },
  { to: "/strategies", label: "Strategies", labelKey: "nav.strategies", icon: ListChecks },
  { to: "/venue-intelligence", label: "Venue Intelligence", labelKey: "nav.venueIntelligence", icon: ArrowLeftRight },
  { to: "/lab", label: "Hussar Lab", labelKey: "nav.hussarLab", icon: FlaskConical },
  { to: "/lab/learning", label: "Learning Report", labelKey: "nav.learningReport", icon: TrendingUp },
  { to: "/arena", label: "Intelligence Arena", labelKey: "nav.intelligenceArena", icon: Swords },
  { to: "/assurance", label: "Assurance", labelKey: "nav.assurance", icon: ShieldCheck },
  { to: "/custody", label: "Custody", labelKey: "nav.custody", icon: Lock },
  { to: "/enterprise/admin", label: "Enterprise", labelKey: "nav.enterprise", icon: Building2, ownerOnly: true },
  { to: "/cash", label: "Cash", labelKey: "nav.cash", icon: Wallet },
  { to: "/crypto", label: "Crypto", labelKey: "nav.crypto", icon: Bitcoin },
  { to: "/traditional", label: "Traditional Assets", labelKey: "nav.traditional", icon: Landmark },
  { to: "/yield", label: "Yield", labelKey: "nav.yield", icon: Percent },
  { to: "/opportunities", label: "Opportunities", labelKey: "nav.opportunities", icon: Crosshair },
  { to: "/wildcard", label: "Wildcard", labelKey: "nav.wildcard", icon: Target },
  { to: "/intelligence", label: "Intelligence", labelKey: "nav.intelligence", icon: Newspaper },
  { to: "/tax-ledger", label: "Tax Ledger", labelKey: "nav.taxLedger", icon: Receipt },
  { to: "/tax-lots", label: "Tax Lots", labelKey: "nav.taxLots", icon: Layers },
  { to: "/transactions", label: "Transactions", labelKey: "nav.transactions", icon: ArrowLeftRight },
  { to: "/journal", label: "Decision Journal", labelKey: "nav.journal", icon: BookOpen },
  { to: "/audit", label: "Audit Ledger", labelKey: "nav.audit", icon: ShieldCheck },
  { to: "/risk", label: "Risk Governor", labelKey: "nav.risk", icon: Sliders },
  { to: "/performance", label: "Performance", labelKey: "nav.performance", icon: TrendingUp },
  { to: "/connections", label: "Connections", labelKey: "nav.connections", icon: LinkIcon },
  { to: "/settings", label: "Settings", labelKey: "nav.settings", icon: Settings },
];

const MODE_META = {
  PAPER: { cls: "bg-blue-500/10 border-blue-500/40 text-blue-400", descKey: "banner.paper" },
  LIVE_CONFIRM: { cls: "bg-amber-500/10 border-amber-500/50 text-amber-400", descKey: "banner.liveConfirm" },
  LIVE_AUTO: { cls: "bg-emerald-500/10 border-emerald-500 text-emerald-400", descKey: "banner.liveAuto" },
};

export default function Layout() {
  const { user, logout } = useAuth();
  const navItems = NAV.filter((n) => !n.ownerOnly || user?.role === "platform_owner");
  const navigate = useNavigate();
  const location = useLocation();
  const [dash, setDash] = useState(null);
  const [killOpen, setKillOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [currentAccount, setCurrentAccount] = useState(() => localStorage.getItem("hussar_account") || "konrad");
  const [online, setOnline] = useState(navigator.onLine);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    const goOffline = () => setOnline(false);
    const goOnline = () => setOnline(true);
    const onUpdate = () => setUpdateAvailable(true);
    window.addEventListener("offline", goOffline);
    window.addEventListener("online", goOnline);
    window.addEventListener("hussar-pwa-update", onUpdate);
    return () => {
      window.removeEventListener("offline", goOffline);
      window.removeEventListener("online", goOnline);
      window.removeEventListener("hussar-pwa-update", onUpdate);
    };
  }, []);

  useEffect(() => {
    api.get("/accounts", { params: { account: undefined } }).then((r) => setAccounts(r.data)).catch(() => {});
  }, []);

  const switchAccount = (slug) => {
    localStorage.setItem("hussar_account", slug);
    setCurrentAccount(slug);
    window.location.reload();
  };

  const load = () => api.get("/dashboard").then((r) => setDash(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  const mode = dash?.kill_switch ? "EMERGENCY_STOP" : dash?.execution_mode || "PAPER";
  const meta = MODE_META[mode] || { cls: "bg-red-500/15 border-red-500 text-red-400", descKey: "banner.emergency" };
  const paperConn = (dash?.connections || []).find((c) => c.provider === "crypto_com_exchange");
  const reconChip = dash?.security?.reconciliation;
  const reconLabel = !reconChip?.status ? "RECONCILIATION: NOT RUN"
    : reconChip.status === "FAIL" ? "RECONCILIATION: FAIL"
    : reconChip.stale ? `RECONCILIATION: STALE${reconChip.age_hours != null ? ` (${Math.round(reconChip.age_hours)}h)` : ""}`
    : "RECONCILIATION: PASS";
  const reconCls = !reconChip?.status || reconChip.stale ? "text-amber-300"
    : reconChip.status === "FAIL" ? "text-red-400" : "text-emerald-400";

  const activateKill = async () => {
    try {
      await api.post("/settings/kill-switch", { confirm: true });
      toast.error("EMERGENCY STOP ACTIVATED — all automated trading halted");
      setKillOpen(false);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <CelebrationProvider>
    <div className="min-h-screen bg-[#0B0C0E] text-slate-100">
      <header className="fixed top-0 inset-x-0 z-50 h-14 bg-[#121316] border-b border-[#2D323E] flex items-center px-3 md:px-4 gap-2 md:gap-4">
        <button data-testid="mobile-nav-toggle" onClick={() => setMenuOpen(true)} className="md:hidden text-slate-400 hover:text-slate-100 shrink-0">
          <Menu className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2 min-w-0">
          <img src="/assets/hussar-mark.png" alt="Hussar Treasury" className="w-8 h-8 shrink-0 object-contain" data-testid="brand-logo" />
          <div className="leading-tight hidden sm:block">
            <div className="font-display font-bold tracking-wide text-sm" data-testid="brand-header">{t("brand.name")}</div>
            <div className="text-[10px] tracking-widest text-slate-500 uppercase hidden md:block">{t("brand.tagline")}</div>
          </div>
        </div>
        <div className="flex-1" />
        {accounts.length > 0 && (
          <Select value={currentAccount} onValueChange={switchAccount}>
            <SelectTrigger data-testid="account-switcher" className="w-28 md:w-36 h-8 bg-[#0B0C0E] border-[#2D323E] text-xs font-semibold">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#17191E] border-[#2D323E]">
              {accounts.map((a) => (
                <SelectItem key={a.id} value={a.id} data-testid={`account-option-${a.id}`}>
                  {a.name}{a.account_type === "CUSTODIAL" ? " (custodial)" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <span className={`badge ${meta.cls} hidden lg:inline-flex`} data-testid="header-mode-badge">{mode.replace("_", "-")}</span>
        <span className="badge bg-slate-500/10 text-slate-400 border-slate-600/60 hidden xl:inline-flex" data-testid="header-connection-status">
          {paperConn ? `Exchange: ${paperConn.status.replace(/_/g, " ")}` : "…"}
        </span>
        <button
          data-testid="emergency-kill-switch-button"
          onClick={() => setKillOpen(true)}
          disabled={dash?.kill_switch}
          className="hidden md:flex items-center gap-2 rounded-sm border border-red-700/70 bg-red-950/40 text-red-400 px-3 py-1.5 text-xs font-bold tracking-wide hover:bg-red-900/50 disabled:opacity-40 transition-colors"
        >
          <OctagonX className="w-4 h-4" /> {t("action.killSwitch")}
        </button>
        <button data-testid="logout-button" onClick={async () => { await logout(); localStorage.removeItem("hussar_account"); sessionStorage.clear(); navigate("/login"); }}
          className="text-slate-500 hover:text-slate-200 transition-colors shrink-0" title={`${t("action.signOut")} (${user?.email})`}>
          <LogOut className="w-4 h-4" />
        </button>
      </header>

      <div data-testid="security-strip" className={`fixed top-14 inset-x-0 z-40 border-b px-3 md:px-4 h-8 text-[10px] md:text-[11px] font-semibold tracking-wide flex items-center gap-3 md:gap-4 whitespace-nowrap overflow-x-auto ${meta.cls}`}>
        <span data-testid="security-execution">EXECUTION: {mode.replace("_", "-")}</span>
        <span className="opacity-80" data-testid="security-risk">RISK: {(dash?.security?.risk_governor || (dash?.kill_switch ? "EMERGENCY_STOP" : "ACTIVE")).replace(/_/g, " ")}</span>
        <span className={dash?.security?.audit_chain === "INVALID" ? "text-red-400" : "opacity-80"} data-testid="security-audit">AUDIT: {dash?.security?.audit_chain || "…"}</span>
        <span className={!dash?.security?.anchor ? "opacity-80" : dash.security.anchor.overdue ? "text-amber-300" : "opacity-80"} data-testid="security-anchor">
          {!dash?.security?.anchor ? "ANCHOR: …"
            : dash.security.anchor.age_hours == null ? "· ANCHOR MISSING"
            : dash.security.anchor.overdue ? "· ANCHOR OVERDUE"
            : `· ANCHORED ${dash.security.anchor.age_hours < 1 ? "<1" : Math.round(dash.security.anchor.age_hours)}h AGO`}
        </span>
        <span className={reconCls} data-testid="security-reconciliation">{reconLabel}</span>
        <span className="opacity-80 hidden sm:inline" data-testid="security-provider">PROVIDER: {(dash?.security?.provider || "…").replace(/_/g, " ")}</span>
        <span className="opacity-80 hidden sm:inline" data-testid="security-automation">AUTOMATION: {dash?.security?.automation || "OFF"}</span>
        {dash?.tax_reserve_underfunded && (
          <span className="text-amber-300" data-testid="tax-underfunded-warning">{t("banner.taxUnderfunded")}</span>
        )}
        <button data-testid="mobile-kill-switch-button" onClick={() => setKillOpen(true)} disabled={dash?.kill_switch}
          className="ml-auto md:hidden flex items-center gap-1.5 text-red-400 border border-red-700/70 bg-red-950/40 rounded-sm px-2 py-0.5 disabled:opacity-40">
          <OctagonX className="w-3.5 h-3.5" /> STOP
        </button>
      </div>

      {!online && (
        <div data-testid="offline-indicator"
          className="fixed top-[92px] inset-x-0 z-40 bg-amber-950/90 border-b border-amber-700/60 text-amber-300 text-[11px] font-semibold tracking-wide px-4 py-1.5 text-center">
          OFFLINE — connection required for current Treasury data
        </div>
      )}

      {updateAvailable && (
        <div data-testid="pwa-update-banner"
          className="fixed bottom-4 right-4 z-[70] panel border-[#D4AF37]/60 px-4 py-3 flex items-center gap-3 shadow-lg">
          <span className="text-[11px] font-bold tracking-widest text-[#D4AF37] uppercase">Hussar Treasury update available</span>
          <button data-testid="pwa-update-apply" onClick={applyUpdate}
            className="text-[11px] font-bold bg-[#D4AF37] text-[#0B0C0E] rounded-sm px-3 py-1 hover:bg-[#C5A059]">APPLY</button>
          <button data-testid="pwa-update-dismiss" onClick={() => setUpdateAvailable(false)}
            className="text-slate-500 hover:text-slate-300"><X className="w-3.5 h-3.5" /></button>
        </div>
      )}

      {menuOpen && (
        <div className="fixed inset-0 z-[60] bg-black/70 md:hidden" onClick={() => setMenuOpen(false)} data-testid="mobile-nav-overlay">
          <div className="w-56 h-full bg-[#101114] border-r border-[#2D323E] pt-4 overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="mobile-nav">
            <div className="flex items-center justify-between px-4 pb-3 border-b border-[#222630]">
              <span className="font-display font-bold text-sm tracking-wide">{t("nav.commandCenter")}</span>
              <button onClick={() => setMenuOpen(false)} className="text-slate-400" data-testid="mobile-nav-close"><X className="w-5 h-5" /></button>
            </div>
            {navItems.map(({ to, label, labelKey, icon: Icon, end }) => (
              <NavLink key={to} to={to} end={end} onClick={() => setMenuOpen(false)} data-testid={`mobile-nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-4 py-2.5 text-[13px] border-l-2 ${isActive ? "text-[#D4AF37] border-[#D4AF37] bg-[#D4AF37]/5" : "text-slate-400 border-transparent"}`}>
                <Icon className="w-4 h-4" /> {t(labelKey)}
              </NavLink>
            ))}
            <div className="px-4 pt-4 mt-2 border-t border-[#222630]" data-testid="mobile-nav-install">
              <InstallAppButton />
            </div>
          </div>
        </div>
      )}

      <div className={`flex ${online ? "pt-[92px]" : "pt-[122px]"}`}>
        <nav className="w-52 shrink-0 fixed left-0 top-[92px] bottom-0 overflow-y-auto border-r border-[#222630] bg-[#101114] py-3 hidden md:block" data-testid="command-center-nav">
          {navItems.map(({ to, label, labelKey, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end} data-testid={`nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-4 py-2 text-[13px] transition-colors border-l-2 ${
                  isActive ? "text-[#D4AF37] border-[#D4AF37] bg-[#D4AF37]/5" : "text-slate-400 border-transparent hover:text-slate-200 hover:bg-[#17191E]"}`
              }>
              <Icon className="w-4 h-4" /> {t(labelKey)}
            </NavLink>
          ))}
        </nav>
        <main className="flex-1 min-w-0 md:ml-52 p-4 sm:p-6 lg:p-8 max-w-[1800px]">
          <ErrorBoundary key={location.pathname}>
            <Outlet context={{ reloadLayout: load, dash }} />
          </ErrorBoundary>
        </main>
      </div>

      <AlertDialog open={killOpen} onOpenChange={setKillOpen}>
        <AlertDialogContent className="bg-[#17191E] border-red-800" data-testid="kill-switch-modal">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-400 font-display">STOP ALL AUTOMATED TRADING</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              This immediately disables all execution, cancels queued transactions, and disables schedulers.
              Portfolio monitoring, accounting, and the tax ledger continue. This event is permanently recorded
              in the audit ledger. This action is deliberate and reversible only through manual reset.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="kill-switch-cancel" className="bg-[#1E2128] border-[#3F4656] text-slate-300">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="kill-switch-confirm" onClick={activateKill}
              className="bg-red-700 hover:bg-red-600 text-white font-bold">CONFIRM EMERGENCY STOP</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </CelebrationProvider>
  );
}
